/* ============================================================
   GEOTECH Main Product - Flexible v4
   Hybrid metafield and native multi-image variant gallery groups.
   Progressive enhancement on top of Dawn's product system.
   ============================================================ */
(() => {
  const ROOT_SELECTOR = 'product-info[data-gvp-root]';
  const text = (node) => (node?.textContent || '').trim();

  function init(root) {
    if (!root || root.dataset.gvpInitialized === 'true') return;
    root.dataset.gvpInitialized = 'true';

    const maxThumbs = Math.max(1, Number(root.dataset.gvpMaxThumbnails) || 5);
    const launchAtMs = Number(root.dataset.gvpLaunchAtMs);
    const legacyLaunchAt = root.dataset.gvpLaunchAt ? Date.parse(root.dataset.gvpLaunchAt) : NaN;
    const launchAt = Number.isFinite(launchAtMs) && launchAtMs > 0 ? launchAtMs : legacyLaunchAt;
    const dialog = root.querySelector('[data-gvp-notify]');
    const sizeChartDialog = root.querySelector('[data-gvp-size-chart]');
    const sizeChartTrigger = root.querySelector('[data-gvp-size-guide]');
    const notifyCopy = root.querySelector('[data-gvp-notify-copy]');
    const notifyTags = root.querySelector('[data-gvp-notify-tags]');
    const notifyForm = root.querySelector('[data-gvp-notify-form]');
    const launchStatus = root.querySelector('[data-gvp-launch-status]');
    const countdown = root.querySelector('[data-gvp-countdown]');
    const countdownDays = countdown?.querySelector('[data-gvp-countdown-days]');
    const countdownHours = countdown?.querySelector('[data-gvp-countdown-hours]');
    const countdownMinutes = countdown?.querySelector('[data-gvp-countdown-minutes]');
    const countdownSeconds = countdown?.querySelector('[data-gvp-countdown-seconds]');
    const buyZone = root.querySelector('[data-gvp-buy-zone]');
    const mediaGallery = root.querySelector('media-gallery[data-gvp-variant-filter]');
    const galleryViewer = mediaGallery?.querySelector('[id^="GalleryViewer-"]');
    const gallerySlider = mediaGallery?.querySelector('.gvp-media-carousel');
    const variantGalleryTemplates = [...root.querySelectorAll('template[data-gvp-variant-gallery-template]')];
    const variantInventoryNode = root.querySelector('[data-gvp-variant-inventory]');
    const mediaOrderNode = root.querySelector('[data-gvp-media-order]');
    const variantInventory = new Map();
    let variantRecords = [];
    let mediaOrder = [];
    try {
      variantRecords = JSON.parse(variantInventoryNode?.textContent || '[]');
      variantRecords.forEach((record) => variantInventory.set(String(record.id), record));
    } catch (error) {
      console.warn('GEOTECH inventory data could not be read.', error);
    }
    try {
      mediaOrder = JSON.parse(mediaOrderNode?.textContent || '[]').map((id) => String(id));
    } catch (error) {
      console.warn('GEOTECH media order data could not be read.', error);
    }
    const variantMediaMode = root.dataset.gvpVariantMediaMode || 'grouped';
    const includeSharedMedia = root.dataset.gvpIncludeSharedMedia === 'true';
    const useVariantGalleryMetafield = root.dataset.gvpUseVariantGalleryMetafield !== 'false';
    const variantIdsByAnchor = new Map();
    const groupVariantIdsByMedia = new Map();

    variantRecords.forEach((record) => {
      if (!record.featuredMediaId) return;
      const mediaId = String(record.featuredMediaId);
      const variantIds = variantIdsByAnchor.get(mediaId) || [];
      variantIds.push(String(record.id));
      variantIdsByAnchor.set(mediaId, variantIds);
    });

    let activeGroupVariantIds = [];
    mediaOrder.forEach((mediaId) => {
      const anchorVariantIds = variantIdsByAnchor.get(mediaId);
      if (anchorVariantIds?.length) activeGroupVariantIds = [...anchorVariantIds];
      groupVariantIdsByMedia.set(mediaId, [...activeGroupVariantIds]);
    });
    const notifyDefaultCopy = text(notifyCopy);
    const labels = {
      stockAvailable: root.dataset.gvpInStockLabel || 'In stock',
      stockSoldout: root.dataset.gvpSoldOutLabel || 'Sold out',
      stockPredrop: root.dataset.gvpPredropStockLabel || 'Drops soon',
      add: root.dataset.gvpAddLabel || 'Add to bag',
      soldout: root.dataset.gvpSoldoutButtonLabel || "Notify me when it's back",
      predrop: root.dataset.gvpPredropButtonLabel || 'Notify me when it drops'
    };
    const showPriceInButton = root.dataset.gvpShowPriceInButton !== 'false';

    let observer;
    let timer;
    let queued = false;
    let notifyReturnFocus = null;
    let sizeChartReturnFocus = null;
    let optionNotifyTimer = null;
    let galleryScrollFrame = null;
    const variantRefreshTimers = new Set();

    const setText = (node, value) => {
      if (!node) return;
      const next = String(value ?? '');
      if ((node.textContent || '') !== next) node.textContent = next;
    };

    const isPredrop = () => Number.isFinite(launchAt) && launchAt > Date.now();

    function selectedOptionValues() {
      return [...root.querySelectorAll('[data-gvp-variants] .product-form__input')]
        .map((group) => {
          const checked = group.querySelector('input:checked');
          if (checked) return String(checked.value || '').trim();
          const select = group.querySelector('select');
          return String(select?.value || '').trim();
        })
        .filter(Boolean);
    }

    function variantFromSelectedOptions() {
      const selectedOptions = selectedOptionValues();
      if (!selectedOptions.length) return null;

      return variantRecords.find((record) =>
        Array.isArray(record.options) &&
        record.options.length === selectedOptions.length &&
        record.options.every((value, index) => String(value) === selectedOptions[index])
      ) || null;
    }

    function productIdInput() {
      return root.querySelector(
        'product-form form[action*="/cart/add"] input[name="id"], form[id^="product-form-"] input[name="id"]'
      );
    }

    /* GEOTECH fix (Sep 2026): keep the form's variant id locked to the options the shopper can see.
       Dawn's product-info.js also updates it after its section fetch; this makes "Add to bag",
       the mobile buy bar and "Buy it now" correct even before/without that fetch. */
    function syncFormVariant() {
      const selected = variantFromSelectedOptions();
      const input = productIdInput();
      if (!selected?.id || !input) return;
      const next = String(selected.id);
      if (input.value === next) return;
      input.value = next;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    function currentVariantId() {
      const selectedVariant = variantFromSelectedOptions();
      if (selectedVariant?.id) {
        root.dataset.gvpSelectedVariantId = String(selectedVariant.id);
        return String(selectedVariant.id);
      }

      const input = productIdInput();
      const urlVariantId = new URL(window.location.href).searchParams.get('variant');
      return String(input?.value || urlVariantId || root.dataset.gvpSelectedVariantId || '');
    }

    function isCurrentVariantSoldOut(nativeButton = null) {
      const record = variantInventory.get(currentVariantId());
      if (record) {
        if (record.available === false) return true;
        if (record.managed === true && Number(record.quantity) <= 0) return true;
        return false;
      }

      return Boolean(
        nativeButton &&
        (nativeButton.disabled || nativeButton.getAttribute('aria-disabled') === 'true')
      );
    }

    function currentPurchaseState(nativeButton = null) {
      if (isPredrop()) return 'predrop';
      if (isCurrentVariantSoldOut(nativeButton)) return 'soldout';
      return 'available';
    }

    function currentPrice() {
      const sale = root.querySelector('.gvp__price .price__sale .price-item--sale');
      if (sale && text(sale)) return text(sale);
      return text(root.querySelector('.gvp__price .price__regular .price-item--regular, .gvp__price .price-item--regular'));
    }

    function setActiveState(state) {
      root.querySelectorAll('[data-gvp-state]').forEach((item) => {
        item.classList.toggle('is-active', item.dataset.gvpState === state);
      });
    }

    function updateInventory(state) {
      root.querySelectorAll('[data-gvp-inventory]').forEach((item) => {
        const label =
          state === 'predrop'
            ? (item.dataset.predropLabel || 'Drops soon')
            : state === 'available'
              ? item.dataset.availableLabel
              : item.dataset.soldoutLabel;

        item.classList.toggle('is-soldout', state === 'soldout');
        item.classList.toggle('is-predrop', state === 'predrop');
        setText(item.querySelector('[data-gvp-inventory-label]'), label);
      });
    }

    function setBuyState(state) {
      root.classList.toggle('gvp--predrop', state === 'predrop');
      root.classList.toggle('gvp--soldout', state === 'soldout');
      root.dataset.gvpAvailabilityState = state;
      if (!buyZone) return;
      buyZone.classList.toggle('is-available', state === 'available');
      buyZone.classList.toggle('is-soldout', state === 'soldout');
      buyZone.classList.toggle('is-predrop', state === 'predrop');
    }

    function updateStock(label) {
      setText(root.querySelector('[data-gvp-stock-badge]'), label);
    }

    const mediaIdFromValue = (value) => {
      const normalized = String(value || '');
      const numericSuffix = normalized.match(/(\d+)$/);
      return numericSuffix ? numericSuffix[1] : normalized;
    };

    function mediaIdsForVariant(variantId) {
      const record = variantInventory.get(String(variantId));
      const featuredMediaId = record?.featuredMediaId ? String(record.featuredMediaId) : '';
      if (!featuredMediaId) return new Set();

      if (variantMediaMode === 'single') return new Set([featuredMediaId]);

      const selectedMediaIds = mediaOrder.filter((mediaId) => {
        const groupVariantIds = groupVariantIdsByMedia.get(mediaId) || [];
        if (groupVariantIds.includes(String(variantId))) return true;
        return includeSharedMedia && groupVariantIds.length === 0;
      });

      if (!selectedMediaIds.length) selectedMediaIds.push(featuredMediaId);
      return new Set(selectedMediaIds);
    }

    function setHidden(node, shouldHide) {
      if (!node || node.hidden === shouldHide) return;
      node.hidden = shouldHide;
    }

    function variantGalleryTemplate(variantId) {
      return variantGalleryTemplates.find((template) => template.dataset.gvpVariantId === String(variantId)) || null;
    }

    function generatedItems(container, variantId) {
      if (!container) return [];
      return [...container.querySelectorAll('[data-gvp-generated-variant-media]')]
        .filter((item) => item.dataset.gvpMetafieldVariantId === String(variantId));
    }

    function removeGeneratedVariantMedia() {
      root.querySelectorAll('[data-gvp-generated-variant-media]').forEach((item) => item.remove());
    }

    function moveTemplateChildren(fragment, selector, destination) {
      const source = fragment.querySelector(selector);
      if (!source || !destination) return;
      [...source.children].forEach((child) => destination.appendChild(child));
    }

    function mountMetafieldVariantGallery(variantId, enabled) {
      const template = enabled && useVariantGalleryMetafield
        ? variantGalleryTemplate(variantId)
        : null;

      if (!template || !mediaGallery) {
        removeGeneratedVariantMedia();
        return new Set();
      }

      const expectedCount = Math.max(0, Number(template.dataset.gvpGalleryCount) || 0);
      if (!expectedCount) {
        removeGeneratedVariantMedia();
        return new Set();
      }

      const slideList = mediaGallery.querySelector('.product__media-list');
      const thumbnailList = mediaGallery.querySelector('.thumbnail-list');
      const dotsContainer = mediaGallery.querySelector('[data-gvp-gallery-dots]');
      const modalContent = root.querySelector('product-modal .product-media-modal__content');
      const mountedSlides = generatedItems(slideList, variantId);
      const mountIsComplete = mountedSlides.length === expectedCount &&
        (!thumbnailList || generatedItems(thumbnailList, variantId).length === expectedCount) &&
        (!dotsContainer || generatedItems(dotsContainer, variantId).length === expectedCount) &&
        (!modalContent || generatedItems(modalContent, variantId).length === expectedCount);

      if (!mountIsComplete) {
        removeGeneratedVariantMedia();
        const fragment = template.content.cloneNode(true);
        moveTemplateChildren(fragment, '[data-gvp-template-slides]', slideList);
        moveTemplateChildren(fragment, '[data-gvp-template-thumbnails]', thumbnailList);
        moveTemplateChildren(fragment, '[data-gvp-template-dots]', dotsContainer);
        moveTemplateChildren(fragment, '[data-gvp-template-modal]', modalContent);
        galleryViewer?.resetPages?.();
        mediaGallery.querySelector('[id^="GalleryThumbnails-"]')?.resetPages?.();
      }

      return new Set(
        generatedItems(slideList, variantId).map((slide) => String(slide.dataset.mediaId || ''))
      );
    }

    function syncGallerySelection(mediaId) {
      if (!mediaGallery || !mediaId) return;

      const slides = [...mediaGallery.querySelectorAll('.product__media-item[data-media-id]:not([hidden])')];
      const activeIndex = slides.findIndex((slide) => slide.dataset.mediaId === mediaId);

      slides.forEach((slide) => {
        slide.classList.toggle('is-active', slide.dataset.mediaId === mediaId);
      });

      mediaGallery.querySelectorAll('[data-gvp-gallery-dot]').forEach((dot) => {
        const isActive = dot.dataset.mediaId === mediaId;
        dot.classList.toggle('is-active', isActive);
        if (isActive) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });

      mediaGallery.querySelectorAll('.thumbnail-list__item[data-target]').forEach((item) => {
        const button = item.querySelector('.thumbnail');
        if (!button) return;
        if (item.dataset.target === mediaId) button.setAttribute('aria-current', 'true');
        else button.removeAttribute('aria-current');
      });

      const controls = mediaGallery.querySelector('[data-gvp-gallery-controls]');
      if (activeIndex >= 0) {
        setText(controls?.querySelector('.slider-counter--current'), String(activeIndex + 1));
        const previousButton = controls?.querySelector('button[name="previous"]');
        const nextButton = controls?.querySelector('button[name="next"]');
        if (previousButton) previousButton.disabled = activeIndex <= 0;
        if (nextButton) nextButton.disabled = activeIndex >= slides.length - 1;
      }
      setText(controls?.querySelector('.slider-counter--total'), String(slides.length));
    }

    function scrollToGallerySlide(slide, behavior = 'smooth') {
      if (!gallerySlider || !slide) return;
      gallerySlider.scrollTo({ left: slide.offsetLeft, behavior });
    }

    function navigateGallery(direction) {
      if (!gallerySlider) return;
      const slides = [...gallerySlider.querySelectorAll('.product__media-item[data-media-id]:not([hidden])')];
      if (slides.length <= 1) return;

      const currentIndex = slides.reduce((closestIndex, slide, index) => (
        Math.abs(slide.offsetLeft - gallerySlider.scrollLeft) < Math.abs(slides[closestIndex].offsetLeft - gallerySlider.scrollLeft)
          ? index
          : closestIndex
      ), 0);
      const nextIndex = Math.max(0, Math.min(slides.length - 1, currentIndex + direction));
      const nextSlide = slides[nextIndex];
      scrollToGallerySlide(nextSlide);
      syncGallerySelection(nextSlide.dataset.mediaId);
    }

    function onGallerySlideChange(event) {
      const slide = event.detail?.currentElement;
      if (!slide || slide.hidden || !slide.dataset.mediaId) return;
      syncGallerySelection(slide.dataset.mediaId);
    }

    function onGalleryScroll() {
      if (!gallerySlider || galleryScrollFrame) return;
      galleryScrollFrame = requestAnimationFrame(() => {
        galleryScrollFrame = null;
        const slides = [...gallerySlider.querySelectorAll('.product__media-item[data-media-id]:not([hidden])')];
        if (!slides.length) return;
        const activeSlide = slides.reduce((closest, slide) => (
          Math.abs(slide.offsetLeft - gallerySlider.scrollLeft) < Math.abs(closest.offsetLeft - gallerySlider.scrollLeft)
            ? slide
            : closest
        ));
        syncGallerySelection(activeSlide.dataset.mediaId);
      });
    }

    function updateVariantMedia() {
      const gallery = mediaGallery;
      if (!gallery) return;

      const variantId = currentVariantId();
      const filteringEnabled = gallery.dataset.gvpVariantFilter === 'true';
      const metafieldMediaKeys = mountMetafieldVariantGallery(variantId, filteringEnabled);
      const hasMetafieldGallery = metafieldMediaKeys.size > 0;
      const slides = [...gallery.querySelectorAll('.product__media-item[data-media-id]')];
      if (!slides.length) return;

      const groupedMediaIds = !hasMetafieldGallery && filteringEnabled && variantId
        ? mediaIdsForVariant(variantId)
        : new Set();
      const matchingSlides = hasMetafieldGallery
        ? slides.filter((slide) => metafieldMediaKeys.has(String(slide.dataset.mediaId || '')))
        : groupedMediaIds.size
          ? slides.filter((slide) => groupedMediaIds.has(mediaIdFromValue(slide.dataset.mediaId)))
          : [];
      const isFiltered = matchingSlides.length > 0;
      const visibleSlides = isFiltered ? matchingSlides : slides;
      const visibleSlideSet = new Set(visibleSlides);
      const matchesVisibleMedia = (value) => {
        if (!isFiltered) return true;
        if (hasMetafieldGallery) return metafieldMediaKeys.has(String(value || ''));
        return groupedMediaIds.has(mediaIdFromValue(value));
      };

      slides.forEach((slide) => {
        const shouldHide = !visibleSlideSet.has(slide);
        setHidden(slide, shouldHide);
        slide.setAttribute('aria-hidden', shouldHide ? 'true' : 'false');
        if (shouldHide) slide.classList.remove('is-active');
      });

      const activeVisibleSlide = visibleSlides.find((slide) => slide.classList.contains('is-active'));
      const selectedRecord = variantInventory.get(String(variantId));
      const featuredMediaId = selectedRecord?.featuredMediaId ? String(selectedRecord.featuredMediaId) : '';
      const featuredSlide = hasMetafieldGallery
        ? null
        : visibleSlides.find((slide) => mediaIdFromValue(slide.dataset.mediaId) === featuredMediaId);
      const targetSlide = isFiltered
        ? (featuredSlide || matchingSlides[0])
        : (activeVisibleSlide || visibleSlides[0]);
      const activeFilterKey = isFiltered
        ? `${hasMetafieldGallery ? 'metafield' : variantMediaMode}:${variantId}:${visibleSlides.map((slide) => slide.dataset.mediaId).join(',')}`
        : `gallery:${variantId || 'all'}`;
      const shouldFocusVariant = isFiltered && gallery.dataset.gvpActiveFilterKey !== activeFilterKey;

      gallery.dataset.gvpActiveFilterKey = activeFilterKey;
      if (targetSlide && (shouldFocusVariant || !activeVisibleSlide)) {
        if (typeof gallery.setActiveMedia === 'function') {
          gallery.setActiveMedia(targetSlide.dataset.mediaId, false);
        } else {
          slides.forEach((slide) => slide.classList.toggle('is-active', slide === targetSlide));
        }
        scrollToGallerySlide(targetSlide, 'auto');
      }

      const selectedSlide = shouldFocusVariant || !activeVisibleSlide
        ? targetSlide
        : (visibleSlides.find((slide) => slide.classList.contains('is-active')) || targetSlide || visibleSlides[0]);
      if (selectedSlide) syncGallerySelection(selectedSlide.dataset.mediaId);

      const thumbnails = [...gallery.querySelectorAll('.thumbnail-list__item[data-target]')];
      const eligibleThumbnails = isFiltered
        ? thumbnails.filter((item) => matchesVisibleMedia(item.dataset.target))
        : thumbnails;
      const eligibleThumbnailSet = new Set(eligibleThumbnails);

      thumbnails.forEach((item) => {
        const eligibleIndex = eligibleThumbnails.indexOf(item);
        const shouldHide = !eligibleThumbnailSet.has(item) || eligibleIndex >= maxThumbs;
        setHidden(item, shouldHide);
        item.setAttribute('aria-hidden', shouldHide ? 'true' : 'false');
        if (shouldHide) item.querySelector('.thumbnail')?.removeAttribute('aria-current');
      });

      const targetThumbnail = selectedSlide
        ? thumbnails.find((item) => item.dataset.target === selectedSlide.dataset.mediaId)
        : null;
      if (targetThumbnail) {
        if (targetThumbnail.hidden) {
          [...eligibleThumbnails].reverse().find((item) => !item.hidden && item !== targetThumbnail)?.setAttribute('hidden', '');
          targetThumbnail.hidden = false;
          targetThumbnail.setAttribute('aria-hidden', 'false');
        }
        thumbnails.forEach((item) => {
          const button = item.querySelector('.thumbnail');
          if (!button) return;
          if (item === targetThumbnail) {
            if (button.getAttribute('aria-current') !== 'true') button.setAttribute('aria-current', 'true');
          } else if (button.hasAttribute('aria-current')) button.removeAttribute('aria-current');
        });
      }

      const viewerControls = gallery.querySelector('#GalleryViewer-' + root.dataset.section + ' .slider-buttons');
      const galleryArrowsEnabled = gallery.dataset.gvpShowArrows !== 'false';
      setHidden(viewerControls, !galleryArrowsEnabled || visibleSlides.length <= 1);
      const selectedSlideIndex = Math.max(0, visibleSlides.indexOf(selectedSlide));
      setText(viewerControls?.querySelector('.slider-counter--current'), String(selectedSlideIndex + 1));
      setText(viewerControls?.querySelector('.slider-counter--total'), String(visibleSlides.length));

      const thumbnailSlider = gallery.querySelector('.thumbnail-slider');
      const thumbnailsEnabled = gallery.dataset.gvpShowThumbnails === 'true';
      setHidden(thumbnailSlider, !thumbnailsEnabled || eligibleThumbnails.length <= 1);

      const dots = [...gallery.querySelectorAll('[data-gvp-gallery-dot]')];
      dots.forEach((dot) => {
        const shouldHide = isFiltered && !matchesVisibleMedia(dot.dataset.mediaId);
        setHidden(dot, shouldHide);
        dot.setAttribute('aria-hidden', shouldHide ? 'true' : 'false');
        if (shouldHide) {
          dot.classList.remove('is-active');
          dot.removeAttribute('aria-current');
        }
      });
      const dotsContainer = gallery.querySelector('[data-gvp-gallery-dots]');
      const dotsEnabled = gallery.dataset.gvpShowDots !== 'false';
      setHidden(dotsContainer, !dotsEnabled || visibleSlides.length <= 1);

      const viewer = gallery.querySelector('#GalleryViewer-' + root.dataset.section);
      if (viewer && typeof viewer.resetPages === 'function') {
        requestAnimationFrame(() => viewer.resetPages());
      }

      root.querySelectorAll('[data-gvp-modal-media]').forEach((item) => {
        const shouldHide = isFiltered && !matchesVisibleMedia(item.dataset.mediaId);
        setHidden(item, shouldHide);
        item.setAttribute('aria-hidden', shouldHide ? 'true' : 'false');
      });

      root.classList.toggle('gvp--variant-media-filtered', isFiltered);
      root.classList.toggle('gvp--variant-metafield-gallery', hasMetafieldGallery);
      root.classList.remove('gvp--variant-media-focused');
    }

    function findSizeGroup() {
      const groups = [...root.querySelectorAll('[data-gvp-variants] fieldset.product-form__input, [data-gvp-variants] .product-form__input')];
      let result = null;
      groups.forEach((group) => {
        const heading = group.querySelector('legend.form__label, .form__label');
        const isSize = /\bsize\b|\bsizes\b/i.test(text(heading));
        group.classList.toggle('gvp-size-group', isSize);
        if (isSize && !result) result = group;
      });
      return result;
    }

    function placeSizeGuide() {
      const guide = root.querySelector('[data-gvp-size-guide]');
      if (!guide) return;
      const group = findSizeGroup();
      const heading = group?.querySelector('legend.form__label, .form__label');
      if (heading && guide.parentElement !== heading) heading.appendChild(guide);
    }

    function openNotify(reason = 'restock', optionLabel = '') {
      if (!dialog) return;
      if (sizeChartDialog?.open) closeSizeChart(false);
      notifyReturnFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      const productHandle = root.dataset.productHandle || 'product';
      const cleanOption = String(optionLabel || '').replace(/[,\n\r]/g, ' ').replace(/\s+/g, ' ').trim();
      if (notifyTags) {
        notifyTags.value = [
          reason === 'predrop' ? 'geotech-drop-alert' : 'geotech-restock',
          `product:${productHandle}`,
          cleanOption ? `option:${cleanOption}` : ''
        ].filter(Boolean).join(',');
      }
      if (cleanOption) {
        setText(
          notifyCopy,
          `Leave your email and we’ll keep ${cleanOption} on your radar for this product.`
        );
      } else if (reason === 'predrop') {
        setText(notifyCopy, 'Leave your email and we’ll keep this drop on your radar.');
      } else {
        setText(
          notifyCopy,
          notifyDefaultCopy || 'Leave your email and we’ll keep this product on your radar.'
        );
      }

      if (typeof dialog.showModal === 'function') {
        if (!dialog.open) dialog.showModal();
      } else dialog.setAttribute('open', '');

      requestAnimationFrame(() => dialog.querySelector('input[type="email"]')?.focus({ preventScroll: true }));
    }

    function closeNotify(restoreFocus = true) {
      if (!dialog) return;
      if (typeof dialog.close === 'function' && dialog.open) dialog.close();
      else dialog.removeAttribute('open');
      if (restoreFocus && notifyReturnFocus?.isConnected) notifyReturnFocus.focus({ preventScroll: true });
      notifyReturnFocus = null;
    }

    function openSizeChart(trigger = sizeChartTrigger) {
      if (!sizeChartDialog) return;
      if (dialog?.open) closeNotify(false);
      sizeChartReturnFocus = trigger instanceof HTMLElement
        ? trigger
        : document.activeElement instanceof HTMLElement
          ? document.activeElement
          : null;
      sizeChartTrigger?.setAttribute('aria-expanded', 'true');

      if (typeof sizeChartDialog.showModal === 'function') {
        if (!sizeChartDialog.open) sizeChartDialog.showModal();
      } else sizeChartDialog.setAttribute('open', '');

      requestAnimationFrame(() => sizeChartDialog.querySelector('[data-gvp-size-chart-close]')?.focus({ preventScroll: true }));
    }

    function closeSizeChart(restoreFocus = true) {
      if (!sizeChartDialog) return;
      if (typeof sizeChartDialog.close === 'function' && sizeChartDialog.open) sizeChartDialog.close();
      else sizeChartDialog.removeAttribute('open');
      sizeChartTrigger?.setAttribute('aria-expanded', 'false');
      if (restoreFocus && sizeChartReturnFocus?.isConnected) sizeChartReturnFocus.focus({ preventScroll: true });
      sizeChartReturnFocus = null;
    }

    function resetNotifySubmit() {
      if (!notifyForm) return;
      window.clearTimeout(notifyForm._gvpSubmitTimer);
      delete notifyForm.dataset.gvpSubmitting;
      notifyForm.removeAttribute('aria-busy');
      const button = notifyForm.querySelector('[type="submit"]');
      if (!button) return;
      if (button.dataset.gvpOriginalLabel) button.textContent = button.dataset.gvpOriginalLabel;
      delete button.dataset.gvpOriginalLabel;
      button.style.removeProperty('min-width');
      button.removeAttribute('aria-busy');
      button.removeAttribute('aria-disabled');
    }

    function onNotifySubmit(event) {
      if (!notifyForm) return;
      if (notifyForm.dataset.gvpSubmitting === 'true') {
        event.preventDefault();
        return;
      }

      const button = event.submitter || notifyForm.querySelector('[type="submit"]');
      notifyForm.dataset.gvpSubmitting = 'true';
      notifyForm.setAttribute('aria-busy', 'true');
      if (button) {
        button.dataset.gvpOriginalLabel = button.textContent.trim();
        button.style.minWidth = `${Math.ceil(button.getBoundingClientRect().width)}px`;
        button.textContent = 'Sending…';
        button.setAttribute('aria-busy', 'true');
        button.setAttribute('aria-disabled', 'true');
      }
      notifyForm._gvpSubmitTimer = window.setTimeout(resetNotifySubmit, 15000);
    }

    const onPageShow = () => resetNotifySubmit();

    function addNotifyButton(nativeButton, reason) {
      const wrap = nativeButton.closest('.product-form__buttons') || nativeButton.parentElement;
      if (!wrap) return;

      let button = wrap.querySelector('.gvp__notify-button');

      if (!button) {
        button = document.createElement('button');
        button.type = 'button';
        button.className = 'gvp__notify-button';

        button.addEventListener('click', () => {
          if (!dialog) return;
          openNotify(button.dataset.gvpReason || 'restock');
        });

        nativeButton.insertAdjacentElement('afterend', button);
      }

      button.dataset.gvpReason = reason;

      if (dialog) {
        button.disabled = false;
        button.removeAttribute('aria-disabled');
        setText(button, reason === 'predrop' ? labels.predrop : labels.soldout);
      } else {
        button.disabled = true;
        button.setAttribute('aria-disabled', 'true');
        setText(
          button,
          reason === 'predrop'
            ? labels.stockPredrop
            : labels.stockSoldout
        );
      }

      nativeButton.classList.add('gvp__native-soldout');
    }

    function removeNotifyButton(nativeButton) {
      nativeButton.classList.remove('gvp__native-soldout');
      nativeButton.closest('.product-form__buttons')?.querySelector('.gvp__notify-button')?.remove();
    }

    function decorateBuyButton() {
      const button = root.querySelector('product-form button[name="add"], product-form .product-form__submit');
      if (!button) return;

      const state = currentPurchaseState(button);

      if (state === 'predrop') {
        addNotifyButton(button, 'predrop');
        updateStock(labels.stockPredrop);
        updateInventory('predrop');
        setActiveState('predrop');
        setBuyState('predrop');
        return;
      }

      if (state === 'soldout') {
        addNotifyButton(button, 'restock');
        updateStock(labels.stockSoldout);
        updateInventory('soldout');
        setActiveState('soldout');
        setBuyState('soldout');
        return;
      }

      removeNotifyButton(button);
      updateStock(labels.stockAvailable);
      updateInventory('available');
      setActiveState('available');
      setBuyState('available');

      const label = button.querySelector('span:not(.loading__spinner)') || button.querySelector('span');
      if (label) setText(label, labels.add);

      if (showPriceInButton) {
        const price = currentPrice();
        if (price) button.dataset.gvpPrice = price;
        else delete button.dataset.gvpPrice;
      } else delete button.dataset.gvpPrice;
    }

    function updateCountdown() {
      if (!launchStatus) return;
      if (!isPredrop()) {
        launchStatus.hidden = true;
        return;
      }
      const total = Math.floor(Math.max(0, launchAt - Date.now()) / 1000);
      const d = Math.floor(total / 86400);
      const h = Math.floor((total % 86400) / 3600);
      const m = Math.floor((total % 3600) / 60);
      const s = total % 60;
      launchStatus.hidden = false;
      setText(countdownDays, String(d).padStart(2, '0'));
      setText(countdownHours, String(h).padStart(2, '0'));
      setText(countdownMinutes, String(m).padStart(2, '0'));
      setText(countdownSeconds, String(s).padStart(2, '0'));
      countdown?.setAttribute(
        'aria-label',
        `${d} days, ${h} hours, ${m} minutes, and ${s} seconds remaining until this product drops`
      );
    }

    function decorate() {
      syncFormVariant();
      updateVariantMedia();
      placeSizeGuide();
      decorateBuyButton();
      updateCountdown();
    }

    function queue() {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => { queued = false; decorate(); });
    }

    function onProductSubmit(event) {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !form.matches('form[action*="/cart/add"]')) return;
      syncFormVariant();
      const button = form.querySelector('button[name="add"], .product-form__submit');
      const state = currentPurchaseState(button);
      if (state === 'available') return;
      event.preventDefault();
      event.stopImmediatePropagation();
      openNotify(state === 'predrop' ? 'predrop' : 'restock');
    }

    function onVariantChange(event) {
      const target = event.target instanceof Element ? event.target : null;
      if (!target?.closest('[data-gvp-variants]')) return;

      queue();
      [50, 300].forEach((delay) => {
        const timerId = window.setTimeout(() => {
          variantRefreshTimers.delete(timerId);
          queue();
        }, delay);
        variantRefreshTimers.add(timerId);
      });
    }

    function onNotifyCancel(event) {
      event.preventDefault();
      closeNotify();
    }

    function onSizeChartCancel(event) {
      event.preventDefault();
      closeSizeChart();
    }

    function onClick(event) {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;

      const galleryArrow = target.closest('[data-gvp-gallery-controls] .slider-button');
      if (galleryArrow && root.contains(galleryArrow)) {
        event.preventDefault();
        event.stopImmediatePropagation();
        navigateGallery(galleryArrow.name === 'previous' ? -1 : 1);
        return;
      }

      const galleryThumbnail = target.closest('.thumbnail-list__item[data-target] .thumbnail');
      if (galleryThumbnail && root.contains(galleryThumbnail)) {
        const thumbnailItem = galleryThumbnail.closest('.thumbnail-list__item[data-target]');
        const mediaId = thumbnailItem?.dataset.target;
        const slide = [...(mediaGallery?.querySelectorAll('.product__media-item[data-media-id]') || [])]
          .find((item) => item.dataset.mediaId === mediaId);
        if (!mediaId || !slide || slide.hidden) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        mediaGallery?.setActiveMedia?.(mediaId, false);
        scrollToGallerySlide(slide);
        syncGallerySelection(mediaId);
        return;
      }

      const galleryDot = target.closest('[data-gvp-gallery-dot]');
      if (galleryDot && root.contains(galleryDot)) {
        event.preventDefault();
        const mediaId = galleryDot.dataset.mediaId;
        const slide = [...(mediaGallery?.querySelectorAll('[data-media-id]') || [])]
          .find((item) => item.dataset.mediaId === mediaId);
        if (!mediaId || !slide) return;
        if (typeof mediaGallery.setActiveMedia === 'function') {
          mediaGallery.setActiveMedia(mediaId, false);
        }
        scrollToGallerySlide(slide);
        syncGallerySelection(mediaId);
        return;
      }

      const sizeGuide = target.closest('[data-gvp-size-guide]');
      if (sizeGuide && root.contains(sizeGuide)) {
        event.preventDefault();
        event.stopPropagation();
        openSizeChart(sizeGuide);
        return;
      }

      if (target.closest('[data-gvp-size-chart-close]') && root.contains(target.closest('[data-gvp-size-chart-close]'))) {
        event.preventDefault();
        closeSizeChart();
        return;
      }
      if (sizeChartDialog && target === sizeChartDialog) {
        closeSizeChart();
        return;
      }

      if (target.closest('[data-gvp-notify-close]') && root.contains(target.closest('[data-gvp-notify-close]'))) {
        event.preventDefault();
        closeNotify();
        return;
      }
      if (dialog && target === dialog) {
        closeNotify();
        return;
      }

      if (target.closest('.product-form__buttons') && root.contains(target)) syncFormVariant();

      const purchaseControl = target.closest('button[name="add"], .shopify-payment-button__button');
      if (purchaseControl && root.contains(purchaseControl)) {
        const nativeButton = root.querySelector('product-form button[name="add"], product-form .product-form__submit');
        const state = currentPurchaseState(nativeButton);
        if (state === 'available') return;
        event.preventDefault();
        event.stopImmediatePropagation();
        openNotify(state === 'predrop' ? 'predrop' : 'restock');
        return;
      }

      const label = target.closest('[data-gvp-variants] label[for]');
      if (!label || !root.contains(label) || !label.closest('.gvp-size-group')) return;
      const input = document.getElementById(label.getAttribute('for'));
      if (!(input instanceof HTMLInputElement) || !root.contains(input)) return;
      const unavailable = input.disabled || input.classList.contains('disabled') || label.classList.contains('disabled') || label.getAttribute('aria-disabled') === 'true';
      if (!unavailable || !dialog) return;

      window.clearTimeout(optionNotifyTimer);
      optionNotifyTimer = window.setTimeout(() => {
        optionNotifyTimer = null;
        queue();
        const optionLabel = selectedOptionValues().join(' / ') || input.value || text(label);
        openNotify(isPredrop() ? 'predrop' : 'restock', optionLabel);
      }, 350);
    }

    root.addEventListener('click', onClick, true);
    root.addEventListener('submit', onProductSubmit, true);
    root.addEventListener('change', onVariantChange);
    galleryViewer?.addEventListener('slideChanged', onGallerySlideChange);
    gallerySlider?.addEventListener('scroll', onGalleryScroll, { passive: true });
    dialog?.addEventListener('cancel', onNotifyCancel);
    sizeChartDialog?.addEventListener('cancel', onSizeChartCancel);
    notifyForm?.addEventListener('submit', onNotifySubmit);
    window.addEventListener('pageshow', onPageShow);

    observer = new MutationObserver(queue);
    observer.observe(root, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['disabled', 'aria-disabled', 'aria-current', 'class', 'value']
    });

    if (Number.isFinite(launchAt)) {
      timer = window.setInterval(() => {
        updateCountdown();
        if (!isPredrop()) {
          queue();
          window.clearInterval(timer);
          timer = null;
        }
      }, 1000);
    }

    root._gvpCleanup = () => {
      observer?.disconnect();
      root.removeEventListener('click', onClick, true);
      root.removeEventListener('submit', onProductSubmit, true);
      root.removeEventListener('change', onVariantChange);
      galleryViewer?.removeEventListener('slideChanged', onGallerySlideChange);
      gallerySlider?.removeEventListener('scroll', onGalleryScroll);
      dialog?.removeEventListener('cancel', onNotifyCancel);
      sizeChartDialog?.removeEventListener('cancel', onSizeChartCancel);
      notifyForm?.removeEventListener('submit', onNotifySubmit);
      window.removeEventListener('pageshow', onPageShow);
      resetNotifySubmit();
      window.clearTimeout(optionNotifyTimer);
      if (galleryScrollFrame) cancelAnimationFrame(galleryScrollFrame);
      variantRefreshTimers.forEach((timerId) => window.clearTimeout(timerId));
      variantRefreshTimers.clear();
      if (timer) window.clearInterval(timer);
      delete root.dataset.gvpInitialized;
    };

    decorate();
    if (root.querySelector('[data-gvp-notify-success]')) requestAnimationFrame(() => openNotify(isPredrop() ? 'predrop' : 'restock'));
  }

  function initAll(scope = document) {
    if (scope.matches?.(ROOT_SELECTOR)) init(scope);
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach(init);
  }

  initAll();
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    if (event.target.matches?.(ROOT_SELECTOR)) event.target._gvpCleanup?.();
    event.target.querySelectorAll?.(ROOT_SELECTOR).forEach((item) => item._gvpCleanup?.());
  });
})();
